/**
 * Per-user (per-session) export for the Built Index funnel.
 *
 * One row per participant: identity, assessment scores, unlock status, and the
 * timestamp of every funnel step they hit — so a single CSV answers "who did
 * what, when, did they pay, and did they get the report and the book?".
 */
import {
  BUILT_INDEX_STEPS,
  type BuiltIndexStep,
} from "@/lib/built-index-analytics";
import type { FunnelEvent } from "@/lib/funnel-segments";
import { deviceOf, campaignOf, referrerOf } from "@/lib/funnel-segments";

export type ParticipantRow = {
  id: string;
  created_at: string;
  full_name: string;
  email: string;
  role?: string | null;
  built_total?: number | null;
  built_tier?: string | null;
  disc_type?: string | null;
  lovin_score?: number | null;
  completed?: boolean;
  paid?: boolean;
};

function norm(email?: string | null): string {
  return (email ?? "").trim().toLowerCase();
}

/** Earliest timestamp per step, keyed by session id and by email. */
function indexEvents(events: FunnelEvent[]) {
  const bySession = new Map<string, FunnelEvent[]>();
  const byEmail = new Map<string, FunnelEvent[]>();
  for (const ev of events) {
    if (ev.session_id) {
      const list = bySession.get(ev.session_id) ?? [];
      list.push(ev);
      bySession.set(ev.session_id, list);
    }
    const email = norm(ev.email);
    if (email) {
      const list = byEmail.get(email) ?? [];
      list.push(ev);
      byEmail.set(email, list);
    }
  }
  return { bySession, byEmail };
}

function firstTimestamps(events: FunnelEvent[]): Partial<Record<BuiltIndexStep, string>> {
  const out: Partial<Record<BuiltIndexStep, string>> = {};
  for (const ev of events) {
    const step = ev.step as BuiltIndexStep;
    if (!BUILT_INDEX_STEPS.includes(step)) continue;
    const current = out[step];
    if (!current || ev.occurred_at < current) out[step] = ev.occurred_at;
  }
  return out;
}

export function buildParticipantExport(
  participants: ParticipantRow[],
  events: FunnelEvent[],
): Record<string, unknown>[] {
  const { bySession, byEmail } = indexEvents(events);

  return participants.map((p) => {
    const own = [
      ...(bySession.get(p.id) ?? []),
      ...(byEmail.get(norm(p.email)) ?? []),
    ];
    const deduped = Array.from(new Map(own.map((e) => [e.id, e])).values()).sort((a, b) =>
      a.occurred_at.localeCompare(b.occurred_at),
    );
    const times = firstTimestamps(deduped);
    const first = deduped[0];
    const last = deduped[deduped.length - 1];

    const unlocked = Boolean(p.paid) || Boolean(times.unlock);
    const downloaded = Boolean(times.report_download);
    const bookDelivered = Boolean(times.book_delivery);

    const row: Record<string, unknown> = {
      session_id: p.id,
      full_name: p.full_name,
      email: p.email,
      role: p.role ?? "",
      started_at: p.created_at,
      first_event_at: first?.occurred_at ?? "",
      last_event_at: last?.occurred_at ?? "",
      device: deviceOf(first?.user_agent),
      referrer: referrerOf(first?.attribution),
      campaign: campaignOf(first?.attribution),
      built_total: p.built_total ?? "",
      built_tier: p.built_tier ?? "",
      disc_type: p.disc_type ?? "",
      lovin_score: p.lovin_score ?? "",
      assessment_completed: p.completed ? "yes" : "no",
      unlock_status: unlocked ? "unlocked" : times.checkout_start ? "checkout_started" : "locked",
      paid: p.paid ? "yes" : "no",
      unlocked_at: times.unlock ?? "",
      report_downloaded: downloaded ? "yes" : "no",
      report_downloaded_at: times.report_download ?? "",
      book_delivered: bookDelivered ? "yes" : "no",
      book_delivered_at: times.book_delivery ?? "",
      steps_completed: Object.keys(times).length,
    };

    for (const step of BUILT_INDEX_STEPS) {
      row[`ts_${step}`] = times[step] ?? "";
    }
    return row;
  });
}
